import store from '@/store';
import { createRouter, createWebHistory } from 'vue-router';
import * as Sentry from '@sentry/vue';

// Containers
const DefaultContainer = () => import('@/containers/DefaultContainer.vue');

// Views
const Page404 = () => import('@/views/Page404.vue');
const Page403 = () => import('@/views/Page403.vue');
const Table = () => import('@/shared/table/Table.vue');
const ModelShow = () => import('@/shared/modelDetail/Show.vue');
const TicketsIndex = () => import('@/views/tickets/Index.vue');
const TicketsShow = () => import('@/views/tickets/Show.vue');
const Login = () => import('@/views/auth/Login.vue');
const ForgotPassword = () => import('@/views/auth/ForgotPassword.vue');
const ResetPassword = () => import('@/views/auth/ResetPassword.vue');

const router = createRouter({
    history: createWebHistory(),
    linkActiveClass: 'open active',
    scrollBehavior: (to) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                if (to.hash) {
                    resolve({
                        selector: to.hash,
                        behavior: 'smooth',
                    });
                }
                resolve({
                    left: 0,
                    top: 0,
                });
            }, 1000);
        });
    },
    routes: [
        {
            path: '/msgraph/code/:code',
            name: 'msgraph_callback',
            beforeEnter (to, from, next) {
                const code = to.params.code;
                if (code) {
                    store.dispatch('login', {
                        username: null,
                        password: null,
                        code,
                    }).then(() => {
                        next({ name: 'tickets.index' });
                    }).catch((error) => {
                        sessionStorage.setItem('msgraph_error', JSON.stringify({
                            message: error?.response?.data?.message
                        }));

                        next({ name: 'login', query: { reason: 'msgraph_failed' } });
                    });
                }
            },
        },
        {
            path: '/',
            redirect: '/tickets',
            name: '',
            beforeEnter: (to, from, next) => {
                store.dispatch('getSystemStatus').then((response) => {
                    return next();
                });
            },
            children: [
                {
                    path: '/login/:guard?',
                    name: 'login',
                    component: Login,
                },
                {
                    path: '/password-reset',
                    name: 'password_reset',
                    component: ForgotPassword,
                },
                {
                    path: '/password/reset/:token',
                    name: 'password_reset_token',
                    component: ResetPassword,
                },
            ],
        },
        {
            path: '/(.*)*', // Route not found
            redirect: '/tickets',
            name: '',
            component: DefaultContainer,
            beforeEnter: (to, from, next) => {
                // Check if token is part of URL, set local storage and remove it from URL
                if (to.query.token) {
                    localStorage.setItem('APP_QUOTE_TOKEN', to.query.token);
                    delete to.query.token;
                }

                /**
                 * - Check system status first.
                 * - If completed, check auth status.
                 */
                store.dispatch('getSystemStatus')
                    .then((response) => {
                        if (response.data.trial_ends_in < 0 || (!localStorage.getItem('APP_QUOTE_TOKEN') && !localStorage.getItem('APP_PORTAL_TOKEN'))) {
                            return next({ name: 'login' });
                        }

                        return store.dispatch('me')
                            .then((resp) => {
                                try {
                                    if (resp.user) {
                                        Sentry.setUser({
                                            id: resp.user.id,
                                            email: resp.user.email,
                                            username: resp.user.full_name
                                        });
                                    }
                                } catch (error) {
                                    console.error('Error setting Sentry user:', error);
                                }

                                if (['403', '404'].indexOf(to.name) >= 0) {
                                    // Skip any checks for error pages.
                                    return next();
                                }

                                return checkPermissions(to)
                                    .then(() => next())
                                    .catch(() => {
                                        return next({
                                            name: '403',
                                            params: [to.path],
                                            query: { r: to.name },
                                        });
                                    });
                            })
                            .catch(() => {
                                store.dispatch('logout').then(() => {
                                    next({ name: 'login' });
                                });
                            });
                    })
                    .catch((error) => {
                        if (error.response && error.response.status === 404) {
                            // Route not found, tenant does not exist.
                            window.location.href = `${import.meta.env.VITE_NOT_FOUND_URL}?d=${store.state.subdomain}`;
                        }

                        throw error;
                    });
            },
            children: [
                {
                    path: '/quotes',
                    name: 'quotes.index',
                    component: Table,
                },
                {
                    path: '/quotes/:id',
                    name: 'quotes.show',
                    component: ModelShow,
                },
                {
                    path: '/tickets',
                    name: 'tickets.index',
                    component: TicketsIndex,
                },
                {
                    path: '/tickets/create',
                    name: 'tickets.store',
                    component: TicketsShow,
                },
                {
                    path: '/tickets/:id',
                    name: 'tickets.show',
                    component: TicketsShow,
                },
                {
                    path: '/subscriptions',
                    name: 'subscriptions.index',
                    component: Table,
                },
                {
                    path: '/subscriptions/:id',
                    name: 'subscriptions.show',
                    component: ModelShow,
                },
                {
                    path: '/assets',
                    name: 'assets.index',
                    component: Table,
                },
                {
                    path: '/assets/:id',
                    name: 'assets.show',
                    component: ModelShow,
                },
                {
                    path: '/contracts',
                    name: 'contracts.index',
                    component: Table,
                },
                {
                    path: '/contracts/:id',
                    name: 'contracts.show',
                    component: ModelShow,
                },
                {
                    path: '/invoices',
                    name: 'invoices.index',
                    component: Table,
                },
                {
                    path: '/:pathMatch(.*)*',
                    name: '404',
                    component: Page404,
                },
                {
                    path: '/:pathMatch(.*)*',
                    name: '403',
                    component: Page403,
                },
            ],
        },
    ],
});

function checkPermissions (to) {
    if (!to.name) {
        return Promise.resolve();
    }
    return store.getters.hasPermission(to.name) ? Promise.resolve() : Promise.reject(new Error('Permission denied.'));
}

export default router;
